<template>
	<div class="table-scroll" tabindex="0" :aria-label="t('comparableAnalysis.table')">
		<p class="reorder-status" role="status" aria-live="polite">{{ reorderStatus }}</p>
		<table :style="{ '--value-column-count': comparables.length + 1 }">
			<thead>
				<tr>
					<th scope="col" class="attribute">{{ t("comparableAnalysis.attributes") }}</th>
					<th scope="col" class="subject">{{ t("comparableAnalysis.subject") }}</th>
					<th
						v-for="(comparable, index) in comparables"
						:key="comparable.inscriptionNumber"
						scope="col"
						class="comparable"
						:class="{ 'drag-over': dragOver === index }"
						@dragover.prevent="dragSource !== null && (dragOver = index)"
						@drop.prevent="drop(index)"
					>
						<div class="heading">
							<button
								v-if="canEdit"
								ref="columnHandles"
								type="button"
								class="icon-button drag-handle"
								:data-inscription="comparable.inscriptionNumber"
								:draggable="!disabled"
								:disabled="disabled"
								:aria-label="
									t('comparableAnalysis.reorderColumn', {
										number: formatInscriptionNumber(comparable.inscriptionNumber),
									})
								"
								:title="t('comparableAnalysis.reorderHelp')"
								@dragstart="startDrag(index, $event)"
								@dragend="resetDrag"
								@keydown.left.prevent="reorder(index, index - 1)"
								@keydown.right.prevent="reorder(index, index + 1)"
							>
								<AppIcon :name="IconName.GripVertical" :size="14" />
							</button>
							<span class="position">#{{ index + 1 }}</span>
							<span class="number">{{ formatInscriptionNumber(comparable.inscriptionNumber) }}</span>
							<RouterLink
								v-if="comparable.data"
								class="icon-button"
								:to="{
									name: Route.Home,
									query: { transaction: comparable.sourceInscriptionNumber ?? comparable.inscriptionNumber },
								}"
								target="_blank"
								rel="noopener"
								:aria-label="t('comparableAnalysis.openTransaction')"
								:title="t('comparableAnalysis.openTransaction')"
							>
								<AppIcon :name="IconName.ExternalLink" :size="14" :stroke-width="1.75" />
							</RouterLink>
							<div v-if="canEdit" class="column-actions">
								<button
									type="button"
									class="icon-button remove"
									:disabled="disabled"
									:aria-label="`${t('comparableAnalysis.remove')} ${formatInscriptionNumber(comparable.inscriptionNumber)}`"
									:title="t('comparableAnalysis.remove')"
									@click="emit('remove', comparable.inscriptionNumber)"
								>
									<AppIcon :name="IconName.CloseSmall" :size="12" :stroke-width="1.5" />
								</button>
							</div>
						</div>
						<span v-if="!comparable.data" class="unavailable">{{ t("comparableAnalysis.unavailable") }}</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<template v-for="group in groups" :key="group.title">
					<tr class="group">
						<th :colspan="comparables.length + 2" scope="colgroup">
							<span class="group-title">{{ t(group.title) }}</span>
						</th>
					</tr>
					<tr v-for="row in group.rows" :key="row.key">
						<th scope="row">{{ label(row) }}</th>
						<td class="subject" :class="{ empty: !isSubjectEditable(row.key) && isEmpty(subject[row.key]) }">
							<ComparableAnalysisSubjectField
								v-if="isSubjectEditable(row.key) && subjectDraft"
								v-model="subjectDraft"
								:field="editableFields.get(row.key)!"
								:title="label(row)"
								:readonly="disabled"
							/>
							<template v-else>{{ display(subject[row.key], row.key) }}</template>
						</td>
						<td
							v-for="comparable in comparables"
							:key="comparable.inscriptionNumber"
							:class="{ empty: isEmpty(comparable.data?.[row.key]) }"
						>
							{{ display(comparable.data?.[row.key], row.key) }}
						</td>
					</tr>
				</template>
			</tbody>
		</table>
	</div>
</template>
<script setup lang="ts">
import { computed, nextTick, ref } from "vue"
import { useI18n } from "vue-i18n"
import { LandSubcategory } from "@/graphql"
import type { ComparableAnalysisComparable, ComparableAnalysisSubject, ComparableAnalysisSubjectInput } from "@/graphql"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import ComparableAnalysisSubjectField from "./ComparableAnalysisSubjectField.vue"
import { comparableAnalysisFields } from "@/utils/comparableAnalysisFields"
import { useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { AreaUnit, convertArea, convertPricePerArea } from "@/utils/unitSystem"
import { formatInscriptionNumber } from "@/utils/format"
import { Route } from "@/utils/routing"
const props = defineProps<{
	subject: ComparableAnalysisSubject
	comparables: ComparableAnalysisComparable[]
	editing?: boolean
	disabled?: boolean
}>()
const subjectDraft = defineModel<ComparableAnalysisSubjectInput>("subjectDraft")
const emit = defineEmits<{
	(event: "reorder", numbers: string[]): void
	(event: "remove", number: string): void
}>()
const { t, locale } = useI18n()
const store = useComparableAnalysisStore()
const canEdit = computed(() => props.editing && store.canWrite)
const editableFields = new Map<string, (typeof comparableAnalysisFields)[number]>(
	comparableAnalysisFields.map(field => [field.key, field])
)
function isSubjectEditable(key: string) {
	return canEdit.value && !!subjectDraft.value && editableFields.has(key)
}
const dragSource = ref<number | null>(null)
const dragOver = ref<number | null>(null)
const columnHandles = ref<HTMLButtonElement[]>([])
const reorderStatus = ref("")
function startDrag(index: number, event: DragEvent) {
	if (!canEdit.value || props.disabled) return event.preventDefault()
	dragSource.value = index
	if (event.dataTransfer) {
		event.dataTransfer.effectAllowed = "move"
		event.dataTransfer.setData("text/plain", props.comparables[index].inscriptionNumber)
	}
}
function resetDrag() {
	dragSource.value = null
	dragOver.value = null
}
function drop(index: number) {
	if (dragSource.value !== null) reorder(dragSource.value, index)
	resetDrag()
}
function reorder(from: number, to: number) {
	if (!canEdit.value || props.disabled || from === to || to < 0 || to >= props.comparables.length) return
	const numbers = props.comparables.map(comparable => comparable.inscriptionNumber)
	const [number] = numbers.splice(from, 1)
	if (!number) return
	numbers.splice(to, 0, number)
	emit("reorder", numbers)
	reorderStatus.value = t("comparableAnalysis.columnMoved", {
		number: formatInscriptionNumber(number),
		position: to + 1,
	})
	void nextTick(() => columnHandles.value.find(handle => handle.dataset.inscription === number)?.focus())
}
const { areaUnit, areaUnitSymbol, pricePerAreaUnitSymbol } = useUnitSystem()
type Row = { key: keyof ComparableAnalysisSubject; label: string }
const fieldLabels = new Map<string, string>(comparableAnalysisFields.map(field => [field.key, field.label]))
const additionalLabels: Record<string, string> = {
	parcelIds: "transaction.card.fields.parcels",
	buyerRepresentatives: "transactions-table.columns.buyer-reps",
	sellerRepresentatives: "transactions-table.columns.seller-reps",
	zoningPlanLink: "comparableAnalysis.zoningPlanLink",
	zoningGridLink: "comparableAnalysis.zoningGridLink",
	urbanPlanLink: "comparableAnalysis.urbanPlanLink",
	padLink: "comparableAnalysis.padLink",
	dataRoomLink: "comparableAnalysis.dataRoomLink",
	project3dModelLink: "comparableAnalysis.project3dModelLink",

	areaSquareFeet: "transaction.card.fields.totalLandArea",
	buildingAreaSqft: "transaction.card.fields.buildingArea",
	density: "transactions-table.columns.density",
	parkingTotal: "transaction.card.fields.parkingTotal",
	parkingRatio: "transaction.card.fields.parkingRatio",
	unitAreaSqft: "transaction.card.fields.unitsArea",
	unitType: "transactions-table.columns.residential-unit-type",
	developableLandAreaSqft: "transaction.card.fields.developableLandArea",
	authorizedUses: "transactions-table.columns.authorized-uses",
	projectTypes: "comparableAnalysis.projectUses",
	pricePerLandSqft: "transaction.card.fields.priceLand",
	pricePerDevelopableSqft: "transaction.card.fields.priceDevelopable",
	pricePerBuildableSqft: "transaction.card.fields.priceBuildable",
	pricePerBuildingSqft: "transaction.card.fields.priceBuilding",
	pricePerUnit: "transaction.card.fields.priceUnit",
	buyerRepresentativeFirstName: "transactions-table.columns.buyer-reps",
	sellerRepresentativeFirstName: "transactions-table.columns.seller-reps",
}
const definitions: { title: string; keys: (keyof ComparableAnalysisSubject)[] }[] = [
	{
		title: "transactions-table.sections.localisation",
		keys: ["parcelIds", "address", "municipalityName", "postalCode", "areaSquareFeet", "buildingAreaSqft"],
	},
	{
		title: "transactions-table.sections.date-parties",
		keys: [
			"deedOfSaleDate",
			"psaDate",
			"saleContext",
			"buyerName",
			"buyerRepresentatives",
			"buyerType",
			"buyerNeq",
			"buyerActivity",
			"sellerName",
			"sellerRepresentatives",
			"sellerType",
			"sellerNeq",
			"sellerActivity",
		],
	},
	{
		title: "transactions-table.sections.type-transaction",
		keys: [
			"assetType",
			"landCategoryNew",
			"landSubcategory",
			"landSubtype",
			"authorizedUses",
			"currentState",
			"isGrossLand",
			"tod",
		],
	},
	{
		title: "transactions-table.sections.prix",
		keys: [
			"salePrice",
			"pricePerLandSqft",
			"pricePerDevelopableSqft",
			"pricePerBuildableSqft",
			"pricePerBuildingSqft",
			"pricePerUnit",
			"broker",
			"agency",
			"marketplace",
			"marketplaceId",
			"mortgage",
			"lender",
			"lenderAmount",
			"salesBalance",
		],
	},
	{
		title: "transaction.card.accordion.zoning",
		keys: ["zoningPlanLink", "zoningGridLink", "urbanPlanLink", "padLink", "dataRoomLink"],
	},
	{
		title: "transactions-table.sections.developpement",
		keys: [
			"codificationType",
			"projectApproval",
			"projectApprovalAtDeedOfSale",
			"projectLink",
			"projectDocument",
			"project3dModelLink",
			"projectTypes",
			"stories",
			"siteCoverageRatio",
			"density",
			"buildableAreaSqft",
			"numberOfUnits",
			"parkingTypes",
			"parkingIndoorCount",
			"parkingOutdoorCount",
			"parkingTotal",
			"parkingRatio",
			"unitType",
			"unitAreaSqft",
			"projectComments",
		],
	},
	{
		title: "transaction.card.accordion.restrictions",
		keys: [
			"cptaq",
			"urbanPerimeter",
			"infrastructure",
			"landShape",
			"parcelReport",
			"contamination",
			"contaminationCost",
			"wetlands",
			"floodableZone",
			"endangeredSpecies",
			"deductionRatio",
			"deductionComments",
			"deductionFiles",
			"developableLandAreaSqft",
			"infrastructureDeductionRatio",
			"infrastructureDeductionComments",
			"netLandAreaSqft",
			"comments",
		],
	},
]
const sensitive = new Set(comparableAnalysisFields.filter(field => field.sensitive).map(field => String(field.key)))
const groups = computed(() =>
	definitions.map(group => ({
		title: group.title,
		rows: group.keys
			.filter(key => store.canReadSensitive || !sensitive.has(key))
			.map(key => ({ key, label: fieldLabels.get(key) ?? additionalLabels[key] ?? key })),
	}))
)
function label(row: Row): string {
	const text = t(row.label)
	if (row.key === "pricePerUnit") return t("transactions-table.columns.price-per-unit")
	if (row.key === "numberOfUnits") return t("transactions-table.columns.unit-count")
	if (row.key === "parkingIndoorCount") return t("transactions-table.columns.indoor-parking")
	if (row.key === "parkingOutdoorCount") return t("transactions-table.columns.outdoor-parking")
	if (row.key === "parkingTotal") return t("transactions-table.columns.total-parking")
	if (row.key === "parkingRatio") return t("transactions-table.columns.parking-ratio")
	if (row.key.startsWith("buyer") && row.key !== "buyerName" && row.key !== "buyerRepresentatives")
		return `${t("transactions-table.columns.buyer")} · ${text}`
	if (row.key.startsWith("seller") && row.key !== "sellerName" && row.key !== "sellerRepresentatives")
		return `${t("transactions-table.columns.seller")} · ${text}`
	if (row.key.startsWith("pricePer") && row.key.endsWith("Sqft")) return `${pricePerAreaUnitSymbol.value} ${text}`
	return row.key.endsWith("Sqft") || row.key === "areaSquareFeet" ? `${text} (${areaUnitSymbol.value})` : text
}
function isEmpty(value: unknown): boolean {
	return value == null || value === "" || (Array.isArray(value) && !value.length)
}
function display(value: unknown, key: string): string {
	if (isEmpty(value)) return "—"
	if (Array.isArray(value)) return value.map(item => display(item, key)).join(" · ")
	if (typeof value === "boolean") return t(value ? "comparableAnalysis.yes" : "comparableAnalysis.no")
	if (typeof value === "number") {
		const converted =
			key.startsWith("pricePer") && key.endsWith("Sqft")
				? convertPricePerArea(value, AreaUnit.SquareFeet, areaUnit.value)
				: key.endsWith("Sqft") || key === "areaSquareFeet"
					? convertArea(value, AreaUnit.SquareFeet, areaUnit.value)
					: value
		const formatted = new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(converted)
		if (["siteCoverageRatio", "deductionRatio", "infrastructureDeductionRatio"].includes(key)) return `${formatted} %`
		return key === "salePrice" || key.endsWith("Cost") || key === "lenderAmount" || key === "pricePerUnit"
			? `${formatted} $`
			: formatted
	}
	if (key === "assetType") return t(`property.assetType.${value}`)
	if (key === "landCategoryNew") return t(`property.landCategory.${value}`)
	if (key === "landSubcategory")
		return t(
			`property.landSubcategory.${Object.entries(LandSubcategory).find(([, item]) => item === value)?.[0] ?? value}`
		)
	return String(value)
}
</script>
<style scoped>
.table-scroll {
	position: relative;
	overflow: auto;
	min-height: 0;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
}
.reorder-status {
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip-path: inset(50%);
}
.icon-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: var(--space-24);
	height: var(--space-24);
	padding: 0;
	border: 0;
	border-radius: var(--border-radius-sm);
	background: transparent;
	color: var(--neutral-600);
	cursor: pointer;
	&:hover:not(:disabled) {
		background: var(--neutral-100);
		color: var(--neutral-900);
	}
	&:focus-visible {
		outline: 2px solid var(--primary-green);
	}
	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
	&.drag-handle {
		cursor: grab;
		color: var(--neutral-400);
	}
	&.remove:hover:not(:disabled) {
		color: var(--accent-warning);
	}
}
table {
	--attribute-column-width: 240px;
	--value-column-min-width: 220px;
	table-layout: fixed;
	min-width: calc(var(--attribute-column-width) + var(--value-column-count) * var(--value-column-min-width));
	border-collapse: separate;
	border-spacing: 0;
	width: 100%;
	font-size: var(--font-size-small);
	color: var(--neutral-900);
	th,
	td {
		padding: var(--spacing-sm) var(--spacing-md);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
		border-right: var(--border-width-thin) solid var(--neutral-100);
		box-sizing: border-box;
		vertical-align: top;
		overflow-wrap: anywhere;
		text-align: left;
	}
	thead th {
		position: sticky;
		top: 0;
		z-index: 2;
		vertical-align: middle;
		background: var(--neutral-50);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		letter-spacing: 0.02em;
		text-transform: uppercase;
		color: var(--neutral-600);
		&.attribute {
			left: 0;
			z-index: 3;
		}
		&.subject {
			left: var(--attribute-column-width);
			z-index: 3;
			color: var(--primary-green);
		}
		&.comparable {
			text-transform: none;
			letter-spacing: normal;
			font-size: var(--font-size-small);
			color: var(--neutral-900);
		}
		&.drag-over {
			box-shadow: inset 3px 0 var(--primary-green);
		}
	}
	.heading {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
		min-height: var(--space-24);
		.position {
			padding: 0 var(--space-6);
			border-radius: var(--border-radius-full);
			background: var(--neutral-200);
			font-size: var(--font-size-xs);
			color: var(--neutral-700);
		}
		.number {
			font-weight: var(--font-weight-semibold);
		}
		.column-actions {
			margin-left: auto;
		}
	}
	.unavailable {
		display: inline-block;
		margin-top: var(--spacing-xs);
		padding: 0 var(--space-6);
		border-radius: var(--border-radius-sm);
		background: var(--neutral-100);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-normal);
		color: var(--neutral-600);
	}
	tbody th {
		position: sticky;
		left: 0;
		z-index: 1;
		background: var(--neutral-0);
		font-weight: var(--font-weight-normal);
		color: var(--neutral-700);
	}
	.attribute,
	tbody th:not([scope="colgroup"]) {
		width: var(--attribute-column-width);
		min-width: var(--attribute-column-width);
		max-width: var(--attribute-column-width);
	}
	.subject {
		border-right-color: var(--neutral-200);
	}
	tbody td.subject {
		position: sticky;
		left: var(--attribute-column-width);
		z-index: 1;
		background: linear-gradient(var(--accent-highlight-light), var(--accent-highlight-light)), var(--neutral-0);
		font-weight: var(--font-weight-medium);
		&:focus-within {
			z-index: 2;
		}
	}
	tbody td.empty {
		color: var(--neutral-400);
	}
	tbody tr:not(.group):hover {
		th,
		td:not(.subject) {
			background: var(--neutral-50);
		}
	}
	.group th {
		position: static;
		padding-top: var(--spacing-md);
		background: var(--neutral-50);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		letter-spacing: 0.02em;
		text-transform: uppercase;
		color: var(--neutral-600);
		.group-title {
			position: sticky;
			left: var(--spacing-md);
			display: inline-block;
		}
	}
}
</style>
